/**
 * Bulk hydration for the CI queues — see `services/pacQueueHydration.ts` for why
 * this exists instead of nested reverse-chains.
 *
 * One search per resource type per chunk of patients NEW to this scope — not one
 * per patient per type, and not one per render. The queues load their whole
 * date-scoped cohort a page at a time, so a render that adds nobody new must
 * cost nothing; see the `heldRef` diff below. Both tabs share the primitives;
 * each asks only for what its columns render.
 */

import { getReferenceString, MedplumClient, resolveId } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import {
  Appointment,
  Bundle,
  CareTeam,
  Communication,
  Coverage,
  EpisodeOfCare,
  Flag,
  HumanName,
  Patient,
  Practitioner,
  QuestionnaireResponse,
  Task,
} from '@medplum/fhirtypes';
import { useEffect, useRef, useState } from 'react';
import {
  appointmentTypeTokens,
  CommunicationCategory,
  CONSULTATION_APPOINTMENT_CODES,
  BARIENDO_CT_SYSTEM,
  COVERAGE_TYPE_CODESYSTEM,
  FlagCategory,
  INSURANCE_COVERAGE_TYPE_CODE,
  PROCEDURE_APPOINTMENT_CODES,
  ProviderRole,
} from '@bariendo/lib/constants';
import { PAC_INTRO_CALL_CODE } from '@bariendo/lib/patient-access';
import { POST_CONSULTATION_LIST_QUESTIONNAIRE_URLS } from '@bariendo/lib/pac-post-consultation-list';
import { PAC_CALL_NOTE_CATEGORIES } from '@bariendo/lib/pac-call-notes';
import { getCareTeamMembersInPool, getPractitionersByRole, toRolePool } from '@bariendo/lib/care-team-roles';
import { ELIGIBILITY_QUESTIONNAIRE_URL } from '@bariendo/lib/eligibility-intake';
import { CLINICAL_TASK_GROUP_IDENTIFIER_TOKEN, OPEN_CLINICAL_TASK_STATUSES } from '@bariendo/lib/clinical-tasks';
import {
  chunk,
  firstByPatient,
  groupByPatient,
  mergeMap,
  LAST_COMMUNICATION_CATEGORIES,
  newRefsOnly,
  searchAllByPatients,
  searchByPatients,
} from '../services/pacQueueHydration';
import { readQueueCache, takeReopenedPatients, writeQueueCache } from '../services/queueHydrationCache';
import { LastCommunicationResult } from '../services/listRowShared';
import { TreatmentPaymentOrder } from '@/components/Timeline/types';
import { reportError } from '@/services/error-reporting';
import { canSearchResourceType } from '@/services/permissions';
import { fetchBot } from '@/services/services';

/** `Appointment` links its patient through a participant, not a top-level field. */
function appointmentPatientRef(appointment: Appointment): string | undefined {
  return appointment.participant?.find((participant) => participant.actor?.reference?.startsWith('Patient/'))?.actor
    ?.reference;
}

function typeToken(codes: readonly string[]): string {
  return appointmentTypeTokens(codes).join(',');
}

export interface PacQueueHydrationNeeds {
  /** Latest live consult per patient — pre-consultation's Consult date column. */
  consult?: boolean;
  /** Latest live intro call per patient, plus its note — post-consultation. */
  introCall?: boolean;
  /** Latest procedure per patient, for the post-consultation exclude. */
  procedure?: boolean;
  /** Notes for intro calls already on the rows — pre-consultation. */
  introCallNoteFor?: string[];
  /**
   * Every intake card on the patient — intro call and PN consultation — newest
   * first. Post-consultation reads Location off whichever exists and the PN
   * consult's Outcome off the one `basedOn` its consult; All inquiry reads
   * Location and Payment method.
   */
  callNotes?: boolean;
  /** The consult-note QuestionnaireResponses the post-consultation tab renders. */
  consultNotes?: boolean;
  /** The eligibility submission — the Location column's last resort (BAR-2597). */
  eligibility?: boolean;
  /** Open clinical tasks, for Next task and the Overdue chip. */
  tasks?: boolean;
  /**
   * Sticky notes, newest first, for the Notes column. Skipped for a role whose
   * policy cannot search Communication: Medplum refuses such a search outright,
   * and one refused search fails the whole hydration.
   */
  stickyNotes?: boolean;
  /** The patient's CareTeam plus the PN pool, for the Patient navigator column. */
  careTeam?: boolean;
  /**
   * The insurer on each patient's insurance Coverage, for the Payment method
   * cell. Skipped for a role whose policy cannot search Coverage, as for
   * `stickyNotes` — today every care-team role (BAR-2930).
   */
  insurancePayer?: boolean;
  /**
   * When each Patient was created — the sign-up date for All inquiry of last
   * resort. Read from the oldest version in the Patient's history, and ONLY for
   * patients this pass found no eligibility submission or episode for: history
   * costs ~10 points a patient, the dominant charge once the queries lost their
   * chains, and on production (2026-09-28) the eligibility form's `authored`
   * covered 1,108 of 1,155 cohort patients, within minutes of creation.
   */
  createdAt?: boolean;
  /**
   * The newest completed call, text or chat per patient, sent on or after this
   * date (`YYYY-MM-DD`). Bulk and paginated to completion, so exact within the
   * floor — see `LAST_COMMUNICATION_LOOKBACK_DAYS` for choosing one.
   */
  lastCommunicationSince?: string;
  /**
   * Each patient's open treatment order, through the payments bot (BAR-2828).
   * Only for a user who `canViewPayments`: the bot refuses anyone else.
   */
  openOrders?: boolean;
  /**
   * Patients already dated without history — carrying `PATIENT_CREATED_AT_EXTENSION`
   * on the query. Skipped by the `createdAt` read. Grows with the cohort, so like
   * `introCallNoteFor` it is not part of the reset key.
   */
  datedPatients?: string[];
}

export interface PacQueueHydration {
  consultByPatient: Map<string, Appointment>;
  /** Every consult per patient, newest first, from the same search — the no-show tab judges on all of them. */
  consultsByPatient: Map<string, Appointment[]>;
  introCallByPatient: Map<string, Appointment>;
  procedureByPatient: Map<string, Appointment>;
  /** Keyed by `Appointment/<id>` of the intro call the note is `basedOn`. */
  introCallNoteByAppointment: Map<string, Communication>;
  /** Both intake-card categories, newest first. */
  callNotesByPatient: Map<string, Communication[]>;
  consultNotesByPatient: Map<string, QuestionnaireResponse[]>;
  /**
   * Newest eligibility submission per patient.
   *
   * One batched search, not one per row: the Location column needs it for every
   * lead on the page, and a per-row read on a list view is a performance problem
   * rather than a display one.
   */
  eligibilityByPatient: Map<string, QuestionnaireResponse>;
  tasksByPatient: Map<string, Task[]>;
  /** Newest sticky note per patient. */
  stickyNoteByPatient: Map<string, Communication>;
  careTeamByPatient: Map<string, CareTeam>;
  /**
   * Reference strings of every practitioner holding the Patient Navigator role.
   * A participant's role is not on the CareTeam — see `care-team-roles.ts`.
   */
  patientNavigatorPool: Set<string>;
  flagsByPatient: Map<string, Flag[]>;
  episodeByPatient: Map<string, EpisodeOfCare>;
  /** The payor Organization's name on the patient's active insurance Coverage; absent while the patient is the payor. */
  insurancePayerByPatient: Map<string, string>;
  /** ISO instant of the Patient's creation version. */
  createdAtByPatient: Map<string, string>;
  /**
   * Newest call, text or chat per patient, with a Practitioner sender's
   * `resource` filled in from `practitionerByRef` — the shape the nested GraphQL
   * chain used to return, so `toLastCommunicationCell` reads either.
   */
  lastCommunicationByPatient: Map<string, LastCommunicationResult>;
  /** Every call, text and chat fetched for Last contact, newest first, for counting attempts. */
  communicationsByPatient: Map<string, Communication[]>;
  /** The open treatment order, for patients who have one. */
  openOrderByPatient: Map<string, TreatmentPaymentOrder>;
  /**
   * Practitioners referenced by hydrated appointments and tasks, by reference.
   *
   * A REST search returns only the matching resources — unlike a GraphQL chain it
   * does not inline `actor.resource` — so the people-column avatars would have no
   * name to render without this second pass.
   */
  practitionerByRef: Map<string, Practitioner>;
  loading: boolean;
}

/** Name for a reference resolved through the hydrated practitioner map. */
export function hydratedPractitionerName(
  reference: string | undefined,
  practitionerByRef: Map<string, Practitioner>
): HumanName | undefined {
  return reference ? practitionerByRef.get(reference)?.name?.[0] : undefined;
}

/** Names for several references, dropping any that did not hydrate. */
export function hydratedPractitionerNames(
  references: string[],
  practitionerByRef: Map<string, Practitioner>
): HumanName[] {
  return references
    .map((reference) => hydratedPractitionerName(reference, practitionerByRef))
    .filter((name): name is HumanName => name !== undefined);
}

/** First Practitioner participant on an appointment fetched over REST. */
export function hydratedAppointmentPractitioner(
  appointment: Appointment | undefined,
  practitionerByRef: Map<string, Practitioner>
): HumanName | undefined {
  const reference = appointment?.participant?.find((participant) =>
    participant.actor?.reference?.startsWith('Practitioner/')
  )?.actor?.reference;

  return hydratedPractitionerName(reference, practitionerByRef);
}

/**
 * A hydration value with everything empty, overridable per field.
 *
 * Exported so tests state only the maps they care about — a row test that had to
 * build every map would mostly assert on its own fixture scaffolding.
 */
export function pacQueueHydration(overrides: Partial<PacQueueHydration> = {}): PacQueueHydration {
  return { ...EMPTY, loading: false, ...overrides };
}

const EMPTY: Omit<PacQueueHydration, 'loading'> = {
  consultByPatient: new Map(),
  consultsByPatient: new Map(),
  introCallByPatient: new Map(),
  procedureByPatient: new Map(),
  introCallNoteByAppointment: new Map(),
  callNotesByPatient: new Map(),
  consultNotesByPatient: new Map(),
  eligibilityByPatient: new Map(),
  tasksByPatient: new Map(),
  stickyNoteByPatient: new Map(),
  careTeamByPatient: new Map(),
  patientNavigatorPool: new Set(),
  flagsByPatient: new Map(),
  episodeByPatient: new Map(),
  insurancePayerByPatient: new Map(),
  createdAtByPatient: new Map(),
  lastCommunicationByPatient: new Map(),
  communicationsByPatient: new Map(),
  openOrderByPatient: new Map(),
  practitionerByRef: new Map(),
};

type HydrationState = Omit<PacQueueHydration, 'loading'>;

/** The maps keyed by patient reference — what a re-hydrated patient must be cleared from. */
const PATIENT_KEYED_MAPS = [
  'consultByPatient',
  'consultsByPatient',
  'introCallByPatient',
  'procedureByPatient',
  'callNotesByPatient',
  'consultNotesByPatient',
  'eligibilityByPatient',
  'tasksByPatient',
  'stickyNoteByPatient',
  'careTeamByPatient',
  'flagsByPatient',
  'episodeByPatient',
  'insurancePayerByPatient',
  'createdAtByPatient',
  'lastCommunicationByPatient',
  'communicationsByPatient',
  'openOrderByPatient',
] as const satisfies readonly (keyof HydrationState)[];

/**
 * The state without any entry for `references`. Merging a re-hydration over the
 * old maps is not enough: `mergeMap` only overwrites keys the new batch has, so a
 * task the rep just completed would otherwise stay on the row.
 */
function withoutPatients(state: HydrationState, references: string[]): HydrationState {
  if (references.length === 0) {
    return state;
  }

  const next = { ...state } as Record<string, unknown>;
  for (const name of PATIENT_KEYED_MAPS) {
    const map = new Map(state[name] as Map<string, unknown>);
    references.forEach((reference) => map.delete(reference));
    next[name] = map;
  }

  return next as unknown as HydrationState;
}

export function usePacQueueHydration(
  patientRefs: string[],
  needs: PacQueueHydrationNeeds,
  scopeKey: string
): PacQueueHydration {
  const medplum = useMedplum();
  const [state, setState] = useState<HydrationState>(EMPTY);
  const [loading, setLoading] = useState(false);

  // What has already been fetched, so a page hydrates only its own additions.
  // A ref, not state: writing it must not itself re-run the effect.
  const heldRef = useRef<{
    scopeKey: string;
    needsKey: string;
    patients: Set<string>;
    notes: Set<string>;
    /** The PN pool is identical for every row, so one run per scope fetches it. */
    poolFetched: boolean;
    /**
     * The subset of `patients` / `notes` whose hydration has LANDED. `patients` is
     * marked before the awaits, so caching it would record an in-flight batch as
     * done and a remount would never fetch it.
     */
    donePatients: Set<string>;
    doneNotes: Set<string>;
  }>({
    scopeKey: '',
    needsKey: '',
    patients: new Set(),
    notes: new Set(),
    poolFetched: false,
    donePatients: new Set(),
    doneNotes: new Set(),
  });

  // `introCallNoteFor` is excluded from the reset key on purpose: it is an array
  // that GROWS with every page, so serialising it here would discard the cache on
  // each page and defeat the diff. It is diffed separately, like `patientRefs`.
  const { introCallNoteFor, datedPatients, ...booleanNeeds } = needs;
  const needsKey = JSON.stringify(booleanNeeds);

  // Serialised so the effect re-runs on CONTENT change, not on the new array
  // identity every render produces. `hasNoteList` rather than the array itself,
  // for the same reason.
  const patientKey = patientRefs.join(',');
  const noteKey = (introCallNoteFor ?? []).join(',');
  const datedKey = (datedPatients ?? []).join(',');
  const hasNoteList = introCallNoteFor !== undefined;

  useEffect(() => {
    const allRefs = patientKey ? patientKey.split(',') : [];
    const allNoteRefs = noteKey ? noteKey.split(',') : [];

    // A different tab or date range is a different population, not a superset.
    // Merging across it would leave the previous queue's patients in the maps.
    //
    // A scope seen in the last few minutes resumes from the cache instead
    // (BAR-2786), less the patients the rep opened since — those, and every
    // intake-card note, are fetched again, since that is what the rep just edited.
    const held = heldRef.current;
    if (held.scopeKey !== scopeKey || held.needsKey !== needsKey) {
      const cacheKey = `${scopeKey}|${needsKey}`;
      const cached = readQueueCache<HydrationState>(cacheKey);

      if (cached) {
        const reopened = takeReopenedPatients(cacheKey);
        const patients = new Set(cached.patients);
        reopened.forEach((reference) => patients.delete(reference));
        const notes = reopened.length > 0 ? new Set<string>() : new Set(cached.notes);

        heldRef.current = {
          scopeKey,
          needsKey,
          patients,
          notes,
          poolFetched: cached.poolFetched,
          donePatients: new Set(patients),
          doneNotes: new Set(notes),
        };
        setState(withoutPatients(cached.state, reopened));
      } else {
        heldRef.current = {
          scopeKey,
          needsKey,
          patients: new Set(),
          notes: new Set(),
          poolFetched: false,
          donePatients: new Set(),
          doneNotes: new Set(),
        };
        setState(EMPTY);
      }
    }

    const refs = newRefsOnly(allRefs, heldRef.current.patients);
    const noteRefs = newRefsOnly(allNoteRefs, heldRef.current.notes);

    // No reset when the list is momentarily empty: it always is on mount, while
    // the root query re-runs, and clearing here would discard a resumed cache.
    // Rows are built from the patient list, so an empty list renders nothing
    // regardless of what the maps hold.
    if (refs.length === 0 && noteRefs.length === 0) {
      setLoading(false);
      return;
    }

    // Recorded before the awaits, so a second page starting while this one is in
    // flight does not re-request the same patients.
    refs.forEach((reference) => heldRef.current.patients.add(reference));
    noteRefs.forEach((reference) => heldRef.current.notes.add(reference));

    // Whether this run's result is still wanted.
    //
    // Deliberately NOT an effect-instance `cancelled` flag. Cleanup fires on every
    // dependency change, not just unmount, and pages land faster than hydration
    // resolves — so an instance flag discarded page N's data the moment page N+1
    // started, while page N's references stayed marked as held. Nothing asks for
    // them again, so those rows keep every derived column as an em-dash forever.
    //
    // Now that hydration MERGES rather than replaces, a late batch is still
    // correct data. Only a scope change invalidates it.
    const runScopeKey = scopeKey;
    const runNeedsKey = needsKey;
    const stillInScope = (): boolean =>
      heldRef.current.scopeKey === runScopeKey && heldRef.current.needsKey === runNeedsKey;

    setLoading(true);

    async function hydrate(): Promise<void> {
      const want: PacQueueHydrationNeeds = JSON.parse(needsKey);

      // Claimed before the awaits so two concurrent pages do not both fetch an
      // identical list. Released in the catch below if this run fails.
      const fetchingPool = Boolean(want.careTeam) && !heldRef.current.poolFetched;
      if (fetchingPool) {
        heldRef.current.poolFetched = true;
      }

      const [
        consults,
        introCalls,
        procedures,
        consultNotes,
        eligibility,
        tasks,
        callNotes,
        careTeams,
        navigatorRoles,
        flags,
        episodes,
        lastCommunications,
        stickyNotes,
        insurancePayerByPatient,
        openOrderByPatient,
      ] = await Promise.all([
        want.consult
          ? searchByPatients<Appointment>(medplum, 'Appointment', 'patient', refs, {
              'appointment-type': typeToken(CONSULTATION_APPOINTMENT_CODES),
              _sort: '-date',
              _count: '1000',
            })
          : Promise.resolve([]),
        want.introCall
          ? searchByPatients<Appointment>(medplum, 'Appointment', 'patient', refs, {
              'appointment-type': typeToken([PAC_INTRO_CALL_CODE]),
              _sort: '-date',
              _count: '1000',
            })
          : Promise.resolve([]),
        want.procedure
          ? searchByPatients<Appointment>(medplum, 'Appointment', 'patient', refs, {
              'appointment-type': typeToken(PROCEDURE_APPOINTMENT_CODES),
              _sort: '-date',
              _count: '1000',
            })
          : Promise.resolve([]),
        want.consultNotes
          ? searchByPatients<QuestionnaireResponse>(medplum, 'QuestionnaireResponse', 'subject', refs, {
              questionnaire: POST_CONSULTATION_LIST_QUESTIONNAIRE_URLS.join(','),
              _sort: '-authored',
              _count: '1000',
            })
          : Promise.resolve([]),
        want.eligibility
          ? searchByPatients<QuestionnaireResponse>(medplum, 'QuestionnaireResponse', 'subject', refs, {
              questionnaire: ELIGIBILITY_QUESTIONNAIRE_URL,
              _sort: '-authored',
              _count: '1000',
            })
          : Promise.resolve([]),
        want.tasks
          ? searchByPatients<Task>(medplum, 'Task', 'patient', refs, {
              'group-identifier': CLINICAL_TASK_GROUP_IDENTIFIER_TOKEN,
              status: OPEN_CLINICAL_TASK_STATUSES.join(','),
              _count: '1000',
            })
          : Promise.resolve([]),
        want.callNotes
          ? searchByPatients<Communication>(medplum, 'Communication', 'subject', refs, {
              category: PAC_CALL_NOTE_CATEGORIES.join(','),
              _sort: '-sent',
              _count: '1000',
            })
          : Promise.resolve([]),
        want.careTeam
          ? searchByPatients<CareTeam>(medplum, 'CareTeam', 'patient', refs, { _count: '1000' })
          : Promise.resolve([]),
        // The PN pool is small and identical for every row, so one unscoped
        // search — and only while it is still empty, so a cohort's pages do not
        // each re-fetch the same list.
        fetchingPool
          ? medplum.searchResources('PractitionerRole', { role: ProviderRole.PATIENT_NAVIGATOR, _count: '1000' })
          : Promise.resolve([]),
        searchByPatients<Flag>(medplum, 'Flag', 'subject', refs, {
          category: `${BARIENDO_CT_SYSTEM}|${FlagCategory.PAC_LEAD_STATUS}`,
          _count: '1000',
        }),
        // Ascending: the EARLIEST episode is the signup date.
        searchByPatients<EpisodeOfCare>(medplum, 'EpisodeOfCare', 'patient', refs, {
          _sort: 'date',
          _count: '1000',
        }),
        want.lastCommunicationSince
          ? searchAllByPatients<Communication>(medplum, 'Communication', 'subject', refs, {
              status: 'completed',
              category: LAST_COMMUNICATION_CATEGORIES.join(','),
              sent: `ge${want.lastCommunicationSince}`,
              _sort: '-sent',
              _count: '1000',
            })
          : Promise.resolve([]),
        want.stickyNotes && canSearchResourceType(medplum, 'Communication')
          ? searchByPatients<Communication>(medplum, 'Communication', 'subject', refs, {
              category: CommunicationCategory.STANDALONE_NOTE,
              _sort: '-sent',
              _count: '1000',
            })
          : Promise.resolve([]),
        want.insurancePayer && canSearchResourceType(medplum, 'Coverage')
          ? fetchInsurancePayers(medplum, refs)
          : Promise.resolve(new Map<string, string>()),
        want.openOrders ? fetchOpenOrders(medplum, refs) : Promise.resolve(new Map<string, TreatmentPaymentOrder>()),
      ]);

      // History only for the patients nothing cheaper dates — see `createdAt`.
      const datedRefs = new Set(
        [
          ...(datedKey ? datedKey.split(',') : []),
          ...eligibility.map((response) => response.subject?.reference),
          ...episodes.map((e) => e.patient?.reference),
        ].filter((reference): reference is string => Boolean(reference))
      );
      const createdAtByPatient = want.createdAt
        ? await fetchCreatedAt(
            medplum,
            refs.filter((reference) => !datedRefs.has(reference))
          )
        : new Map<string, string>();

      // The intro-call notes hang off the intro calls, so they need a second
      // round trip — still one request, not one per row.
      // `noteRefs` is already this page's diff; the fallback covers the tabs that
      // do not pass a list and derive it from what they just fetched.
      const noteBasedOn = hasNoteList ? noteRefs : introCalls.map((a) => `Appointment/${a.id}`).filter(Boolean);
      const introCallNotes = noteBasedOn.length
        ? (
            await Promise.all(
              chunkRefs(noteBasedOn).map((refsChunk) =>
                medplum.searchResources('Communication', {
                  'based-on': refsChunk.join(','),
                  category: CommunicationCategory.PAC_INTRO_CALL_NOTE,
                  _sort: '-sent',
                  _count: '1000',
                })
              )
            )
          ).flat()
        : [];

      // Only the PNs on each team need names; the rest of the team is not rendered.
      const patientNavigatorPool = toRolePool(getPractitionersByRole(navigatorRoles, ProviderRole.PATIENT_NAVIGATOR));
      const navigatorRefs = careTeams.flatMap((careTeam) => getCareTeamMembersInPool(careTeam, patientNavigatorPool));

      // Practitioners referenced by anything hydrated above, in one more search.
      const practitionerRefs = [
        ...new Set(
          [...introCalls, ...consults, ...procedures]
            .flatMap((appointment) => appointment.participant ?? [])
            .map((participant) => participant.actor?.reference)
            .concat(tasks.map((task) => task.owner?.reference))
            .concat(lastCommunications.map((communication) => communication.sender?.reference))
            .concat(stickyNotes.map((note) => note.sender?.reference))
            .concat(navigatorRefs)
            .filter((reference): reference is string => Boolean(reference?.startsWith('Practitioner/')))
        ),
      ];

      const practitioners = practitionerRefs.length
        ? (
            await Promise.all(
              chunkRefs(practitionerRefs).map((refsChunk) =>
                medplum.searchResources('Practitioner', {
                  _id: refsChunk.map((reference) => reference.split('/')[1]).join(','),
                  _count: '1000',
                })
              )
            )
          ).flat()
        : [];

      if (!stillInScope()) {
        return;
      }

      // This page only. Folded onto what earlier pages hydrated rather than
      // replacing it — a page's searches cover its own patients, so assigning
      // would blank every derived column on the rows already on screen.
      const practitionerByRef = new Map(practitioners.map((p) => [`Practitioner/${p.id}`, p]));

      const page = {
        consultByPatient: firstByPatient(consults, appointmentPatientRef),
        consultsByPatient: groupByPatient(consults, appointmentPatientRef),
        introCallByPatient: firstByPatient(introCalls, appointmentPatientRef),
        procedureByPatient: firstByPatient(procedures, appointmentPatientRef),
        introCallNoteByAppointment: firstByPatient(introCallNotes, (note) => note.basedOn?.[0]?.reference),
        callNotesByPatient: groupByPatient(callNotes, (note) => note.subject?.reference),
        consultNotesByPatient: groupByPatient(consultNotes, (note) => note.subject?.reference),
        // `-authored` sort plus first-wins keeps the newest submission for a lead
        // who filled the form more than once.
        eligibilityByPatient: firstByPatient(eligibility, (response) => response.subject?.reference),
        tasksByPatient: groupByPatient(tasks, (task) => task.for?.reference),
        // `-sent` plus first-wins keeps the newest.
        stickyNoteByPatient: firstByPatient(stickyNotes, (note) => note.subject?.reference),
        careTeamByPatient: firstByPatient(careTeams, (careTeam) => careTeam.subject?.reference),
        patientNavigatorPool,
        flagsByPatient: groupByPatient(flags, (flag) => flag.subject?.reference),
        episodeByPatient: firstByPatient(episodes, (episode) => episode.patient?.reference),
        insurancePayerByPatient,
        createdAtByPatient,
        lastCommunicationByPatient: new Map(
          [...firstByPatient(lastCommunications, (communication) => communication.subject?.reference)].map(
            ([patientRef, communication]) => [patientRef, withSenderResource(communication, practitionerByRef)]
          )
        ),
        communicationsByPatient: groupByPatient(
          lastCommunications,
          (communication) => communication.subject?.reference
        ),
        openOrderByPatient,
        practitionerByRef,
      };

      setState((previous) => ({
        consultByPatient: mergeMap(previous.consultByPatient, page.consultByPatient),
        consultsByPatient: mergeMap(previous.consultsByPatient, page.consultsByPatient),
        introCallByPatient: mergeMap(previous.introCallByPatient, page.introCallByPatient),
        procedureByPatient: mergeMap(previous.procedureByPatient, page.procedureByPatient),
        introCallNoteByAppointment: mergeMap(previous.introCallNoteByAppointment, page.introCallNoteByAppointment),
        callNotesByPatient: mergeMap(previous.callNotesByPatient, page.callNotesByPatient),
        consultNotesByPatient: mergeMap(previous.consultNotesByPatient, page.consultNotesByPatient),
        eligibilityByPatient: mergeMap(previous.eligibilityByPatient, page.eligibilityByPatient),
        tasksByPatient: mergeMap(previous.tasksByPatient, page.tasksByPatient),
        stickyNoteByPatient: mergeMap(previous.stickyNoteByPatient, page.stickyNoteByPatient),
        careTeamByPatient: mergeMap(previous.careTeamByPatient, page.careTeamByPatient),
        // Identical for every row, so whichever page fetched it wins.
        patientNavigatorPool: new Set([...previous.patientNavigatorPool, ...page.patientNavigatorPool]),
        flagsByPatient: mergeMap(previous.flagsByPatient, page.flagsByPatient),
        episodeByPatient: mergeMap(previous.episodeByPatient, page.episodeByPatient),
        insurancePayerByPatient: mergeMap(previous.insurancePayerByPatient, page.insurancePayerByPatient),
        createdAtByPatient: mergeMap(previous.createdAtByPatient, page.createdAtByPatient),
        lastCommunicationByPatient: mergeMap(previous.lastCommunicationByPatient, page.lastCommunicationByPatient),
        communicationsByPatient: mergeMap(previous.communicationsByPatient, page.communicationsByPatient),
        openOrderByPatient: mergeMap(previous.openOrderByPatient, page.openOrderByPatient),
        practitionerByRef: mergeMap(previous.practitionerByRef, page.practitionerByRef),
      }));
      refs.forEach((reference) => heldRef.current.donePatients.add(reference));
      noteRefs.forEach((reference) => heldRef.current.doneNotes.add(reference));
      setLoading(false);
    }

    hydrate().catch(() => {
      if (!stillInScope()) {
        return;
      }

      // Release the marks. Leaving them would make the failure permanent: these
      // references would read as already hydrated, so no later run would retry
      // them. The rows keep their base columns and degrade to em-dashes, which
      // is recoverable; being silently excluded from every future fetch is not.
      refs.forEach((reference) => heldRef.current.patients.delete(reference));
      noteRefs.forEach((reference) => heldRef.current.notes.delete(reference));
      heldRef.current.poolFetched = false;
      setLoading(false);
    });
  }, [medplum, patientKey, noteKey, datedKey, needsKey, scopeKey, hasNoteList]);

  // Kept current for the next mount of this scope. Only what has landed is
  // recorded as held; see `donePatients`.
  useEffect(() => {
    const held = heldRef.current;
    if (!held.scopeKey) {
      return;
    }

    writeQueueCache<HydrationState>(`${held.scopeKey}|${held.needsKey}`, {
      state,
      patients: held.donePatients,
      notes: held.doneNotes,
      poolFetched: held.poolFetched,
    });
  }, [state]);

  return { ...state, loading };
}

/**
 * A REST search does not inline `sender.resource` the way the GraphQL chain did,
 * so a Practitioner sender is resolved from the hydrated directory. A sender that
 * does not resolve stays bare, which `toLastCommunicationCell` reads as automated
 * — the conservative direction it already documents.
 */
function withSenderResource(
  communication: Communication,
  practitionerByRef: Map<string, Practitioner>
): LastCommunicationResult {
  const { sender } = communication;

  if (!sender) {
    return { ...communication, sender: undefined };
  }

  const practitioner = sender.reference ? practitionerByRef.get(sender.reference) : undefined;

  return { ...communication, sender: { reference: sender.reference, display: sender.display, resource: practitioner } };
}

/**
 * Delegates to the shared chunker. This file used to carry its own with a
 * hardcoded 50 while `pacQueueHydration.ts` used `HYDRATION_CHUNK_SIZE`; two
 * chunk sizes inside one hydration pass is how they drift.
 */
function chunkRefs(refs: string[]): string[][] {
  return chunk(refs);
}

/**
 * The insurer per patient: the name of the payor Organization on the newest
 * active insurance Coverage. bariendo-be writes the patient as payor until
 * signup resolves the insurer (BAR-2905), and that Coverage names no payer.
 */
async function fetchInsurancePayers(medplum: MedplumClient, refs: string[]): Promise<Map<string, string>> {
  const coverages = await searchByPatients<Coverage>(medplum, 'Coverage', 'patient', refs, {
    type: `${COVERAGE_TYPE_CODESYSTEM}|${INSURANCE_COVERAGE_TYPE_CODE}`,
    status: 'active',
    _sort: '-_lastUpdated',
    _count: '1000',
  });

  // `-_lastUpdated` plus first-wins keeps the newest Coverage.
  const payorByPatient = new Map<string, string>();
  firstByPatient(coverages, (coverage) => coverage.beneficiary?.reference).forEach((coverage, patientRef) => {
    const payor = coverage.payor?.find((reference) => reference.reference?.startsWith('Organization/'))?.reference;
    if (payor) {
      payorByPatient.set(patientRef, payor);
    }
  });

  const organizationRefs = [...new Set(payorByPatient.values())];
  if (organizationRefs.length === 0 || !canSearchResourceType(medplum, 'Organization')) {
    return new Map();
  }

  const organizations = (
    await Promise.all(
      chunkRefs(organizationRefs).map((refsChunk) =>
        medplum.searchResources('Organization', {
          _id: refsChunk.map((reference) => resolveId({ reference }) ?? '').join(','),
          _count: '1000',
        })
      )
    )
  ).flat();
  const nameByRef = new Map(
    organizations
      .filter((organization) => organization.name)
      .map((organization) => [getReferenceString(organization), organization.name as string])
  );

  const payers = new Map<string, string>();
  payorByPatient.forEach((payor, patientRef) => {
    const name = nameByRef.get(payor);
    if (name) {
      payers.set(patientRef, name);
    }
  });
  return payers;
}

/** The bot's page limit for `orderStatusByPatients`. */
const OPEN_ORDERS_PAGE = 100;

/**
 * Open treatment orders per patient, a hundred patients per bot call (BAR-2828).
 * A failed read leaves the column empty rather than failing the hydration.
 */
async function fetchOpenOrders(medplum: MedplumClient, refs: string[]): Promise<Map<string, TreatmentPaymentOrder>> {
  const byPatient = new Map<string, TreatmentPaymentOrder>();

  try {
    const pages = await Promise.all(
      chunk(refs, OPEN_ORDERS_PAGE).map((refsChunk) =>
        fetchBot<{ orders: { medplumPatientId: string; order: TreatmentPaymentOrder | null }[] }>(
          medplum,
          'create-treatment-payment',
          { operation: 'orderStatusByPatients', patients: refsChunk.map((reference) => ({ reference })) }
        )
      )
    );
    for (const { medplumPatientId, order } of pages.flatMap((page) => page.orders)) {
      if (order) {
        byPatient.set(getReferenceString({ resourceType: 'Patient', id: medplumPatientId }), order);
      }
    }
  } catch (error) {
    reportError(error, { component: 'usePacQueueHydration', extra: { read: 'openOrders' } });
  }

  return byPatient;
}

/**
 * Versions to read per patient. Medplum returns history newest-first, so the
 * creation version is the LAST entry; a patient with more versions than this
 * yields the oldest fetched instead — an upper bound on the true sign-up date,
 * which can only push a lead OUT of the seven-day window, never in. An Inquiry
 * lead with two hundred edits is not a real case.
 */
const HISTORY_VERSIONS = 200;

/**
 * Creation instants, from the oldest version in each Patient's history.
 *
 * Nothing else on a self-pay patient records when they signed up: no episode,
 * no stage tag, and `RESOURCE_CREATION_DATE_EXTENSION` is re-stamped on every
 * stage run. History is the record. One `batch` Bundle per chunk of patients
 * keeps this at one request per page rather than one per row.
 */
async function fetchCreatedAt(medplum: MedplumClient, refs: string[]): Promise<Map<string, string>> {
  const createdAt = new Map<string, string>();

  const responses = await Promise.all(
    chunkRefs(refs).map((refsChunk) =>
      medplum.executeBatch({
        resourceType: 'Bundle',
        type: 'batch',
        entry: refsChunk.map((reference) => ({
          request: { method: 'GET', url: `${reference}/_history?_count=${HISTORY_VERSIONS}` },
        })),
      })
    )
  );

  for (const response of responses) {
    for (const entry of response.entry ?? []) {
      const history = entry.resource as Bundle<Patient> | undefined;
      const versions = history?.entry ?? [];
      const oldest = versions[versions.length - 1]?.resource;
      const reference = oldest?.id ? `Patient/${oldest.id}` : undefined;
      const instant = oldest?.meta?.lastUpdated;

      if (reference && instant) {
        createdAt.set(reference, instant);
      }
    }
  }

  return createdAt;
}
